import { atom, createStore } from 'jotai';

import { DEBUG } from 'src/config';

import { destroyPerfEventReporter, initPerfEventReporter } from './reporter';
import { PerfWindowSnapshot } from './types';

import { getAggregator, getCurrentSnapshot, onWindowReport } from './index';

const SNAPSHOT_INTERVAL_MS = 1_000;

let snapshotTimerId: ReturnType<typeof setInterval> | null = null;
let windowReportUnsubscribe: (() => void) | null = null;
let pageLeaveCleanup: (() => void) | null = null;

/** 현재 활성 window의 snapshot (1초 간격 갱신) */
export const perfCurrentWindowAtom = atom<PerfWindowSnapshot | null>(null);

/** 직전에 완료(종료)된 window의 snapshot — 리포트 비교용 */
export const perfLastReportAtom = atom<PerfWindowSnapshot | null>(null);

/**
 * initPerfMonitor() 호출 이후 1회 호출.
 * snapshot 타이머를 구동하고 window 종료 리포트를 atom으로 중계한다.
 */
export const initPerfStore = (jotaiStore: ReturnType<typeof createStore>): void => {
  if (snapshotTimerId != null || windowReportUnsubscribe != null) return;

  const reporter = initPerfEventReporter();

  windowReportUnsubscribe = onWindowReport((snap) => {
    if (DEBUG) jotaiStore.set(perfLastReportAtom, snap);
    reporter.report(snap, 'transition');
    reporter.onWindowTransition();
  });

  snapshotTimerId = setInterval(() => {
    const agg = getAggregator();
    if (!agg) return;
    if (DEBUG) {
      jotaiStore.set(perfCurrentWindowAtom, agg.getCurrentSnapshot({ withSamples: true }));
    }
    reporter.tickSegment(agg);
  }, SNAPSHOT_INTERVAL_MS);

  const handleBeforeUnload = () => {
    const current = getCurrentSnapshot();
    if (current && current.endedAt == null) {
      reporter.report(current, 'page_leave');
    }
  };

  window.addEventListener('beforeunload', handleBeforeUnload);
  pageLeaveCleanup = () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
};

/**
 * snapshot 타이머를 정지한다. HMR/테스트에서 중복 실행 방지용.
 */
export const destroyPerfStore = (): void => {
  windowReportUnsubscribe?.();
  windowReportUnsubscribe = null;

  if (snapshotTimerId != null) {
    clearInterval(snapshotTimerId);
    snapshotTimerId = null;
  }

  pageLeaveCleanup?.();
  pageLeaveCleanup = null;

  destroyPerfEventReporter();
};
